import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAdminAdditionalInfo } from '../../hooks/useAdminAdditionalInfo.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const emptyEditorState = {
  html: '',
  mode: 'visual'
};

const AdditionalInfoSection = ({ onNotify }) => {
  const {
    items,
    loading,
    error,
    refresh,
    saveInfo
  } = useAdminAdditionalInfo();

  const [selectedProcessId, setSelectedProcessId] = useState('');
  const [editorState, setEditorState] = useState(emptyEditorState);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  useLucideIcons([items.length, selectedProcessId, editorState.mode, saving]);

  const processOptions = useMemo(() => items || [], [items]);
  const selectedItem = useMemo(
    () => items.find((item) => String(item.id) === String(selectedProcessId)) || null,
    [items, selectedProcessId]
  );

  useEffect(() => {
    if (!selectedProcessId && items.length) {
      setSelectedProcessId(String(items[0].id));
    }
  }, [items, selectedProcessId]);

  useEffect(() => {
    if (!selectedItem) {
      setEditorState(emptyEditorState);
      return;
    }
    setEditorState({
      html: selectedItem.html_content || '',
      mode: editorState.mode
    });
  }, [selectedItem]);

  useEffect(() => {
    if (editorState.mode !== 'visual' || !editorRef.current) {
      return;
    }
    if (editorRef.current.innerHTML !== editorState.html) {
      editorRef.current.innerHTML = editorState.html;
    }
  }, [editorState.html, editorState.mode]);

  const handleProcessChange = (event) => {
    setSelectedProcessId(event.target.value);
  };

  const handleHtmlChange = (event) => {
    setEditorState((prev) => ({ ...prev, html: event.target.value }));
  };

  const handleVisualInput = () => {
    const html = editorRef.current?.innerHTML || '';
    setEditorState((prev) => ({ ...prev, html }));
  };

  const handleModeChange = (nextMode) => {
    setEditorState((prev) => ({ ...prev, mode: nextMode }));
  };

  const applyCommand = (command, value = null) => {
    if (editorState.mode !== 'visual') {
      return;
    }
    document.execCommand(command, false, value);
    handleVisualInput();
  };

  const handleCreateLink = () => {
    if (editorState.mode !== 'visual') {
      return;
    }
    const url = window.prompt('URL del enlace');
    if (!url) {
      return;
    }
    applyCommand('createLink', url);
  };

  const handleTriggerImageUpload = () => {
    if (editorState.mode !== 'visual') {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await fetch('/admin/api/process-info/upload-image', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudo subir la imagen.');
      }
      if (data?.image_url) {
        applyCommand('insertImage', data.image_url);
      }
      onNotify?.({ type: 'success', message: 'Imagen subida e insertada.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo subir la imagen.' });
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const handleSave = async () => {
    if (!selectedItem) {
      onNotify?.({ type: 'warning', message: 'Selecciona un proceso válido.' });
      return;
    }
    setSaving(true);
    try {
      const saved = await saveInfo({
        processId: selectedItem.id,
        htmlContent: editorState.html || ''
      });
      if (saved) {
        refresh();
      }
      onNotify?.({ type: 'success', message: 'Información adicional guardada.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo guardar la información.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section id="additional-info-section" className="admin-section active">
      <div className="section-header">
        <div className="section-title-group">
          <div className="section-eyebrow">Soporte operativo</div>
          <h2 className="section-title">Info adicional</h2>
          <p className="section-subtitle">
            Agrega manuales o notas específicas por proceso. Se mostrarán debajo de las cards de pronóstico.
          </p>
        </div>
        <div className="section-actions">
          <button className="btn-primary" type="button" onClick={handleSave} disabled={saving || !selectedItem}>
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>

      <div className="dashboard-card full-width">
        <div className="card-header">
          <h3 className="card-title">
            <i data-lucide="file-text" />
            Contenido por proceso
          </h3>
        </div>
        <div className="card-content">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner" />
              <div className="loading-text">Cargando procesos...</div>
            </div>
          ) : error ? (
            <div className="form-error" role="alert">{error}</div>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label" htmlFor="process-info-select">Proceso</label>
                <select
                  id="process-info-select"
                  className="form-select"
                  value={selectedProcessId}
                  onChange={handleProcessChange}
                >
                  {processOptions.map((process) => (
                    <option key={process.id} value={process.id}>
                      {process.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="info-editor-tabs">
                <button
                  type="button"
                  className={`tab-btn ${editorState.mode === 'visual' ? 'active' : ''}`}
                  onClick={() => handleModeChange('visual')}
                >
                  Editor visual
                </button>
                <button
                  type="button"
                  className={`tab-btn ${editorState.mode === 'html' ? 'active' : ''}`}
                  onClick={() => handleModeChange('html')}
                >
                  HTML directo
                </button>
              </div>

              <div className={`info-editor ${editorState.mode}`}>
                <div className="wysiwyg-toolbar">
                  <button type="button" onClick={() => applyCommand('bold')}><strong>B</strong></button>
                  <button type="button" onClick={() => applyCommand('italic')}><em>I</em></button>
                  <button type="button" onClick={() => applyCommand('underline')}><span style={{ textDecoration: 'underline' }}>U</span></button>
                  <button type="button" onClick={() => applyCommand('insertUnorderedList')}><i data-lucide="list" /></button>
                  <button type="button" onClick={() => applyCommand('insertOrderedList')}><i data-lucide="list-ordered" /></button>
                  <button type="button" onClick={handleCreateLink}><i data-lucide="link" /></button>
                  <button type="button" onClick={handleTriggerImageUpload} disabled={uploadingImage}>
                    <i data-lucide="image" />
                  </button>
                  <button type="button" onClick={() => applyCommand('removeFormat')}><i data-lucide="eraser" /></button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleImageUpload}
                />
                {editorState.mode === 'visual' ? (
                  <div
                    ref={editorRef}
                    className="wysiwyg-editor"
                    contentEditable
                    suppressContentEditableWarning
                    onInput={handleVisualInput}
                  />
                ) : (
                  <textarea
                    className="form-input html-editor"
                    rows={12}
                    value={editorState.html}
                    onChange={handleHtmlChange}
                    placeholder="<h3>Manual</h3><p>Describe el proceso...</p>"
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default AdditionalInfoSection;
